<template>
  <div ref="wrapperRef">
    <slot />

    <teleport to="body">
      <VPopup
        v-if="visibleLocal"
        :target="target"
        :visible="visibleLocal"
        :offset="offset"
        :no-pointer="noPointer"
        :show-pointer="!noPointer"
        :position="position"
        :valign="valign"
        :align="align"
        :css-classes="cssClasses"
        @update:visible="onUpdateVisible"
      >
        <slot name="content">
          <div v-if="text" class="mds-popover-content">{{ text }}</div>
        </slot>
      </VPopup>
    </teleport>
  </div>
</template>

<style lang="scss" src="./VPopover.scss" />

<script>
import VPopup from "@/lib/components/VPopup/VPopup.vue";
import Constants from "@/core/misc/Constants.js";

export default {
	name: "VPopover",

	components: {
		VPopup
	},

	props: {
		/**
		 * Триггерное событие, на которое поповер реагирует. Может быть:
		 * none - не реагировать на события, управлять с помощью visible.sync
		 * hover - показывать при наведении на целевой элемент, прятать при уведении
		 * click - показывать по клику на целевом элементе, прятать при клике за границей
		 * поповера или целевого элемента
		 * hover-popup - показывать при наведении, прятать только когда мышь уходит и с элемента и с popup
		 */
		trigger: {
			type: String,
			default: "hover",
			validator: (value) => Constants.POPOVER_TRIGGERS.includes(value),
		},
		/**
		 * Показан ли поповер, может быть синхронизирован
		 */
		visible: {
			type: Boolean,
			default: false,
		},
		/**
		 * Контент поповера может быть передан через это свойство, либо через v-slot:content
		 */
		text: {
			type: String,
			default: null,
		},
		/**
		 * Отступ попапа от целевого элемента. Может быть отрицательным
		 */
		offset: {
			type: Number,
			default: 10,
		},
		/**
		 * Запрещает отрисовку указателя попапа
		 */
		noPointer: {
			type: Boolean,
			default: false,
		},
		/**
		 * Позиция. По умолчанию попап отрисовывается снизу. Возможные значения `top, right, bottom, left`
		 */
		position: {
			type: String,
			default: "bottom",
			validator(value) {
				return Constants.POPUP_POSITIONS.includes(value);
			},
		},
		/**
		 * Вертикальное выравнивание. Работает если position равен `right` или `left`
		 */
		valign: {
			type: String,
			default: "auto",
			validator(value) {
				return Constants.POPUP_VALIGNS.includes(value);
			},
		},
		/**
		 * горизонтальное выравнивание. Работает если position равен `top` или `bottom`
		 */
		align: {
			type: String,
			default: "auto",
			validator(value) {
				return Constants.POPUP_ALIGNS.includes(value);
			},
		},
		/**
		 * Костыль для передачи классов в экземпляр компонента
		 */
		cssClasses: {
			type: Array,
			default: null,
		},
	},

	emits: ['update:visible'],

	setup(props, { emit }) {
		const wrapperRef = ref(null);
		const visibleLocal = ref(props.visible);
		const target = ref(null);
		const popupElement = ref(null);

		// Синхронизация с props.visible
		watch(() => props.visible, (value) => {
			visibleLocal.value = value;
		});

		// Эмит изменений наружу
		watch(visibleLocal, (value) => {
			emit("update:visible", value);
		});

		const show = () => {
			visibleLocal.value = true;
		};

		const hide = () => {
			visibleLocal.value = false;
		};

		const showOnPopupMode = () => {
			if (visibleLocal.value === false) {
				visibleLocal.value = true;
			}
		};

		const hideOnPopupMode = (event) => {
			const x = event.clientX || event.pageX;
			const y = event.clientY || event.pageY;
			const hoverElement = document.elementFromPoint(x, y);

			// Проверяем что мышь не над целевым элементом и не над popup
			if (!target.value?.contains(hoverElement) &&
				!getPopupElement()?.contains(hoverElement)) {
				visibleLocal.value = false;
			}
		};

		const onUpdateVisible = (value) => {
			visibleLocal.value = value;
		};

		const getPopupElement = () => {
			// Ищем popup в DOM по уникальным характеристикам
			const popups = document.querySelectorAll('.mds-popup.visible');
			// Возвращаем последний видимый popup (наш)
			return popups[popups.length - 1] || null;
		};

		const addTriggers = () => {
			if (!target.value) return;

			switch (props.trigger) {
				case "hover-popup":
					target.value.addEventListener("mouseenter", showOnPopupMode);
					target.value.addEventListener("mouseleave", hideOnPopupMode);
					// Для popup элемента добавим слушатель после его появления
					if (props.trigger === "hover-popup") {
						// Отложенное добавление слушателя на popup
						nextTick(() => {
							const popupEl = getPopupElement();
							if (popupEl) {
								popupEl.addEventListener("mouseleave", hideOnPopupMode);
								popupElement.value = popupEl;
							}
						});
					}
					break;
				case "hover":
					target.value.addEventListener("mouseenter", show);
					target.value.addEventListener("mouseleave", hide);
					break;
				case "click":
					target.value.addEventListener("click", show);
					break;
			}
		};

		const removeTriggers = () => {
			if (!target.value) return;

			switch (props.trigger) {
				case "hover-popup":
					target.value.removeEventListener("mouseenter", showOnPopupMode);
					target.value.removeEventListener("mouseleave", hideOnPopupMode);
					if (popupElement.value) {
						popupElement.value.removeEventListener("mouseleave", hideOnPopupMode);
					}
					break;
				case "hover":
					target.value.removeEventListener("mouseenter", show);
					target.value.removeEventListener("mouseleave", hide);
					break;
				case "click":
					target.value.removeEventListener("click", show);
					break;
			}
		};

		// Обновляем слушатели на popup при изменении видимости
		watch(visibleLocal, (newVal) => {
			if (newVal && props.trigger === "hover-popup") {
				nextTick(() => {
					const popupEl = getPopupElement();
					if (popupEl && popupEl !== popupElement.value) {
						// Удаляем старый слушатель
						if (popupElement.value) {
							popupElement.value.removeEventListener("mouseleave", hideOnPopupMode);
						}
						// Добавляем новый
						popupEl.addEventListener("mouseleave", hideOnPopupMode);
						popupElement.value = popupEl;
					}
				});
			}
		});

		onMounted(() => {
			// Находим целевой элемент - первый дочерний элемент
			target.value = wrapperRef.value?.firstElementChild || wrapperRef.value;

			if (!target.value) {
				console.error("VPopover: target element not found");
				return;
			}

			addTriggers();
		});

		onBeforeUnmount(() => {
			removeTriggers();
		});

		return {
			wrapperRef,
			visibleLocal,
			target,
			onUpdateVisible,
		};
	},
};
</script>
